import {
  Scene,
  Vector3,
  HemisphericLight,
  DirectionalLight,
  Color3,
  Color4,
} from '@babylonjs/core';
import { TycoonObjects, MachineType, MACHINE_CONFIGS, PlacedMachine } from './TycoonObjects';
import { TycoonHUD, TycoonStats } from './TycoonHUD';
import { Player } from '../../player/Player';
import { InputManager } from '../../core/InputManager';

interface GridSlot {
  x: number;
  z: number;
  occupied: boolean;
  machine?: PlacedMachine;
}

export class TycoonGame {
  private scene: Scene;
  private objects: TycoonObjects;
  private hud: TycoonHUD;
  private player: Player | null = null;
  private inputManager: InputManager;

  private money: number = 100;
  private totalEarned: number = 0;
  private machines: PlacedMachine[] = [];
  private grid: GridSlot[][] = [];
  private gridSize: number = 8;
  private slotSize: number = 3;

  private isRunning: boolean = false;
  private lastIncomeTime: number = 0;
  private incomeInterval: number = 1000; // 1 second

  private onExitCallback: (() => void) | null = null;

  // Background music
  private backgroundMusic: HTMLAudioElement | null = null;
  private musicVolume: number = 0.3;

  constructor(scene: Scene) {
    this.scene = scene;
    this.objects = new TycoonObjects(scene);
    this.hud = new TycoonHUD();
    this.inputManager = InputManager.getInstance();

    this.initializeGrid();
    this.setupHUD();
  }

  private initializeGrid(): void {
    this.grid = [];
    const halfGrid = this.gridSize / 2;

    for (let x = 0; x < this.gridSize; x++) {
      this.grid[x] = [];
      for (let z = 0; z < this.gridSize; z++) {
        this.grid[x][z] = {
          x: (x - halfGrid + 0.5) * this.slotSize,
          z: (z - halfGrid + 0.5) * this.slotSize,
          occupied: false,
        };
      }
    }

    // Reserve center area for decoration
    const center = Math.floor(this.gridSize / 2);
    for (let dx = -1; dx <= 0; dx++) {
      for (let dz = -1; dz <= 0; dz++) {
        if (this.grid[center + dx] && this.grid[center + dx][center + dz]) {
          this.grid[center + dx][center + dz].occupied = true;
        }
      }
    }
  }

  private setupHUD(): void {
    this.hud.onExit(() => {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    });

    this.hud.onBuyMachine((type: MachineType) => {
      return this.buyMachine(type);
    });

    this.updateHUD();
  }

  public initialize(): void {
    // Setup scene
    this.scene.clearColor = new Color4(0.7, 0.85, 1, 1);

    // Setup lighting
    this.setupLighting();

    // Create factory environment
    this.objects.createGround();

    // Create grid indicators
    this.createGridIndicators();

    // Create player
    const canvas = this.scene.getEngine().getRenderingCanvas();
    if (canvas) {
      const spawnPoint = this.getSpawnPoint();
      this.player = new Player(this.scene, null, canvas, { spawnPosition: spawnPoint });
      // Start in third-person mode so player can see their character
      this.player.setCameraMode('third-person');
    }
  }

  private setupLighting(): void {
    // Ambient light - softer for indoor factory feel
    const ambient = new HemisphericLight(
      'ambient',
      new Vector3(0, 1, 0),
      this.scene
    );
    ambient.intensity = 0.4;
    ambient.diffuse = new Color3(1, 0.98, 0.95);
    ambient.groundColor = new Color3(0.6, 0.55, 0.7);

    // Main directional light (sun) - reduced intensity
    const sun = new DirectionalLight(
      'sun',
      new Vector3(-0.5, -1, -0.5),
      this.scene
    );
    sun.intensity = 0.5;
    sun.diffuse = new Color3(1, 0.95, 0.85);
  }

  private createGridIndicators(): void {
    // Visual indicators for where machines can be placed
    // These are subtle floor markings
  }

  private findFreeSlot(): GridSlot | null {
    for (let x = 0; x < this.gridSize; x++) {
      for (let z = 0; z < this.gridSize; z++) {
        if (!this.grid[x][z].occupied) {
          return this.grid[x][z];
        }
      }
    }
    return null;
  }

  private buyMachine(type: MachineType): boolean {
    const config = MACHINE_CONFIGS[type];

    // Check if unlocked
    if (this.totalEarned < config.unlockAt) {
      return false;
    }

    // Check if can afford
    if (this.money < config.cost) {
      return false;
    }

    // Find free slot
    const slot = this.findFreeSlot();
    if (!slot) {
      this.hud.showNotification('🚫 Sem espaço! A fábrica está cheia.', 'warning');
      return false;
    }

    // Deduct money
    this.money -= config.cost;

    // Create machine
    const position = new Vector3(slot.x, 0, slot.z);
    const machine = this.objects.createMachine(type, position);

    // Mark slot as occupied
    slot.occupied = true;
    slot.machine = machine;

    // Add to machines list
    this.machines.push(machine);

    // Update HUD
    this.updateHUD();

    return true;
  }

  private calculateIncomePerSecond(): number {
    let income = 0;
    for (const machine of this.machines) {
      const config = MACHINE_CONFIGS[machine.type];
      // Level multiplier: each level adds 20% more income
      const levelMultiplier = 1 + (machine.level - 1) * 0.2;
      income += config.income * levelMultiplier;
    }
    return income;
  }

  private checkFloorCollision(): void {
    if (!this.player) return;

    const playerPos = this.player.getPosition();
    const physics = this.player.getPhysics();
    const floorY = 0; // Factory floor level

    // If player fell below the floor, reset them
    if (playerPos.y < floorY - 1) {
      physics.setGroundedAt(floorY);
      return;
    }

    // Check if player is falling and near the floor
    const isNearFloor = playerPos.y <= floorY + 1.5;
    const isAboveFloor = playerPos.y >= floorY - 0.5;
    const isFalling = physics.velocity.y <= 0;

    if (isNearFloor && isAboveFloor && isFalling) {
      physics.setGroundedAt(floorY);
    }
  }

  public update(deltaTime: number): void {
    if (!this.isRunning) return;

    // Update player
    if (this.player) {
      this.player.update(deltaTime);

      // Check floor collision (no voxel world, so we handle it manually)
      this.checkFloorCollision();

      // Keep player inside factory bounds
      const pos = this.player.getPosition();
      const bounds = 22; // Factory is about 50x50, so bounds are ~25 from center
      let clamped = false;
      let newX = pos.x;
      let newZ = pos.z;

      if (pos.x < -bounds) { newX = -bounds; clamped = true; }
      if (pos.x > bounds) { newX = bounds; clamped = true; }
      if (pos.z < -bounds) { newZ = -bounds; clamped = true; }
      if (pos.z > bounds) { newZ = bounds; clamped = true; }

      if (clamped) {
        this.player.setPosition(new Vector3(newX, pos.y, newZ));
      }
    }

    // Update HUD with pointer lock state
    this.hud.setPointerLocked(this.inputManager.isPointerLockedState());

    const now = Date.now();

    // Process income every second
    if (now - this.lastIncomeTime >= this.incomeInterval) {
      const incomePerSecond = this.calculateIncomePerSecond();

      if (incomePerSecond > 0) {
        this.money += incomePerSecond;
        this.totalEarned += incomePerSecond;
        this.updateHUD();
      }

      this.lastIncomeTime = now;
    }
  }

  private updateHUD(): void {
    const stats: TycoonStats = {
      money: this.money,
      totalEarned: this.totalEarned,
      machineCount: this.machines.length,
      incomePerSecond: this.calculateIncomePerSecond(),
    };
    this.hud.updateStats(stats);
  }

  private initBackgroundMusic(): void {
    this.backgroundMusic = new Audio('/assets/music/tycoon-background.mp3');
    this.backgroundMusic.loop = true;
    this.backgroundMusic.volume = this.musicVolume;
  }

  private playBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.play().catch(() => {
        // Browser may block autoplay, will play on first user interaction
        const playOnInteraction = () => {
          this.backgroundMusic?.play();
          document.removeEventListener('click', playOnInteraction);
          document.removeEventListener('keydown', playOnInteraction);
        };
        document.addEventListener('click', playOnInteraction);
        document.addEventListener('keydown', playOnInteraction);
      });
    }
  }

  private stopBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
    }
  }

  public start(): void {
    this.isRunning = true;
    this.lastIncomeTime = Date.now();
    this.hud.show();

    // Initialize and play background music
    this.initBackgroundMusic();
    this.playBackgroundMusic();

    // Welcome message
    setTimeout(() => {
      this.hud.showNotification('🏭 Bem-vindo à sua Fábrica de Doces!', 'info');
    }, 500);
    setTimeout(() => {
      this.hud.showNotification('🛒 Compre máquinas na loja para começar!', 'info');
    }, 2000);
  }

  public stop(): void {
    this.isRunning = false;
    this.hud.hide();
    this.stopBackgroundMusic();
  }

  public onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  public getSpawnPoint(): Vector3 {
    // Spawn near entrance, inside the factory
    return new Vector3(0, 1, 18);
  }

  public getPlayer(): Player | null {
    return this.player;
  }

  public dispose(): void {
    this.stop();
    this.objects.dispose();
    this.hud.dispose();
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.src = '';
      this.backgroundMusic = null;
    }
  }
}
